export function CommonMistakes() {
  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Common Mistakes to Avoid When Selling
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Learning from others' mistakes is among the most valuable tips for selling an apartment building 
            successfully. These common errors cost sellers thousands or even hundreds of thousands of dollars 
            in lost profits, delayed sales, or failed transactions. Avoid these pitfalls to maximize your 
            success and minimize stress during the selling process.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-red-500">
            <div className="flex items-start mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-price-tag-3-line text-2xl text-red-600"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Overpricing Your Property</h3>
                <p className="text-gray-600 text-sm mb-3">
                  <strong>The Mistake:</strong> Setting an unrealistic asking price based on emotion, outdated 
                  comparables, or wishful thinking rather than current market data and property fundamentals.
                </p>
                <p className="text-gray-600 text-sm mb-3">
                  <strong>The Cost:</strong> Overpriced properties sit on the market for months, become stale 
                  listings, and ultimately sell for less than they would have if priced correctly initially. 
                  You lose negotiating leverage and attract only bargain hunters.
                </p>
                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="text-sm font-semibold text-gray-900 mb-2">How to Avoid:</p>
                  <ul className="text-gray-600 text-xs space-y-1">
                    <li>• Obtain professional appraisals or broker opinions of value</li>
                    <li>• Analyze recent comparable sales in your market</li>
                    <li>• Calculate value using income capitalization method</li>
                    <li>• Price competitively to generate multiple offers</li>
                    <li>• Be willing to adjust price if market feedback indicates overpricing</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-orange-500">
            <div className="flex items-start mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-file-damage-line text-2xl text-orange-600"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Poor Documentation and Record Keeping</h3>
                <p className="text-gray-600 text-sm mb-3">
                  <strong>The Mistake:</strong> Failing to maintain organized, comprehensive records of income, 
                  expenses, leases, maintenance, and capital improvements. Providing incomplete or disorganized 
                  information to buyers.
                </p>
                <p className="text-gray-600 text-sm mb-3">
                  <strong>The Cost:</strong> Buyers lose confidence, question your management competence, and 
                  reduce offers to account for uncertainty. Deals fall apart during due diligence when 
                  documentation can't support asking price.
                </p>
                <div className="bg-orange-50 p-4 rounded-lg">
                  <p className="text-sm font-semibold text-gray-900 mb-2">How to Avoid:</p>
                  <ul className="text-gray-600 text-xs space-y-1">
                    <li>• Maintain detailed financial records from day one of ownership</li>
                    <li>• Keep all leases, amendments, and tenant correspondence organized</li>
                    <li>• Document all capital improvements with receipts and permits</li>
                    <li>• Create a comprehensive due diligence package before listing</li>
                    <li>• Use property management software to track all information</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-green-500">
            <div className="flex items-start mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-tools-line text-2xl text-green-600"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Neglecting Property Maintenance</h3>
                <p className="text-gray-600 text-sm mb-3">
                  <strong>The Mistake:</strong> Deferring maintenance and repairs in the months or years before 
                  selling, thinking you'll save money by letting the buyer handle issues. Presenting a property 
                  that looks tired or poorly maintained.
                </p>
                <p className="text-gray-600 text-sm mb-3">
                  <strong>The Cost:</strong> Buyers heavily discount for deferred maintenance, often by 2-3 times 
                  the actual repair cost. Properties in poor condition attract only value-add investors seeking 
                  deep discounts, not premium buyers.
                </p>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm font-semibold text-gray-900 mb-2">How to Avoid:</p>
                  <ul className="text-gray-600 text-xs space-y-1">
                    <li>• Maintain property consistently throughout ownership</li>
                    <li>• Address critical repairs before listing</li>
                    <li>• Invest in curb appeal improvements</li>
                    <li>• Clean and paint common areas</li>
                    <li>• Fix obvious defects that signal poor management</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-indigo-500">
            <div className="flex items-start mb-4">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-shield-cross-line text-2xl text-indigo-600"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Hiding Problems or Providing False Information</h3>
                <p className="text-gray-600 text-sm mb-3">
                  <strong>The Mistake:</strong> Concealing known defects, misrepresenting income or expenses, 
                  or providing misleading information to make the property appear more attractive than it actually is.
                </p>
                <p className="text-gray-600 text-sm mb-3">
                  <strong>The Cost:</strong> Deals collapse when buyers discover deception during due diligence. 
                  You face potential lawsuits for fraud or misrepresentation. Your reputation is destroyed, 
                  making future transactions difficult.
                </p>
                <div className="bg-indigo-50 p-4 rounded-lg">
                  <p className="text-sm font-semibold text-gray-900 mb-2">How to Avoid:</p>
                  <ul className="text-gray-600 text-xs space-y-1">
                    <li>• Disclose all known defects and issues upfront</li>
                    <li>• Provide accurate, verifiable financial information</li>
                    <li>• Be transparent about property challenges</li>
                    <li>• Work with reputable professionals who value honesty</li>
                    <li>• Remember that buyers will discover problems during inspections</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-teal-500">
            <div className="flex items-start mb-4">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-time-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Poor Timing and Rushed Sales</h3>
                <p className="text-gray-600 text-sm mb-3">
                  <strong>The Mistake:</strong> Rushing to sell without proper preparation, listing during poor 
                  market conditions, or making hasty decisions under pressure. Not allowing adequate time for 
                  marketing and buyer competition.
                </p>
                <p className="text-gray-600 text-sm mb-3">
                  <strong>The Cost:</strong> Rushed sales result in lower prices, poor terms, and accepting 
                  suboptimal offers. You leave money on the table and may regret the decision later.
                </p>
                <div className="bg-teal-50 p-4 rounded-lg">
                  <p className="text-sm font-semibold text-gray-900 mb-2">How to Avoid:</p>
                  <ul className="text-gray-600 text-xs space-y-1">
                    <li>• Plan your sale 6-12 months in advance when possible</li>
                    <li>• Prepare property and documentation before listing</li>
                    <li>• Time your sale for optimal market conditions</li>
                    <li>• Allow adequate marketing time to generate competition</li>
                    <li>• Don't let desperation drive decision-making</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-amber-500">
            <div className="flex items-start mb-4">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-user-unfollow-line text-2xl text-amber-600"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Not Using Qualified Professionals</h3>
                <p className="text-gray-600 text-sm mb-3">
                  <strong>The Mistake:</strong> Trying to save money by handling the sale yourself without 
                  experienced brokers, attorneys, or advisors. Using inexperienced professionals who lack 
                  commercial real estate expertise.
                </p>
                <p className="text-gray-600 text-sm mb-3">
                  <strong>The Cost:</strong> Costly mistakes in pricing, marketing, negotiation, legal issues, 
                  and tax planning. The money saved on professional fees is dwarfed by the money lost through 
                  inexperience and errors.
                </p>
                <div className="bg-amber-50 p-4 rounded-lg">
                  <p className="text-sm font-semibold text-gray-900 mb-2">How to Avoid:</p>
                  <ul className="text-gray-600 text-xs space-y-1">
                    <li>• Hire experienced commercial real estate brokers</li>
                    <li>• Work with attorneys specializing in commercial transactions</li>
                    <li>• Consult with CPAs knowledgeable about real estate taxation</li>
                    <li>• Use qualified intermediaries for 1031 exchanges</li>
                    <li>• Invest in professional photography and marketing materials</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Additional Mistakes That Cost Sellers Money
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-center mb-3">
                <i className="ri-close-circle-line text-red-500 text-xl mr-2"></i>
                <h4 className="font-semibold text-gray-900">Ignoring Market Feedback</h4>
              </div>
              <p className="text-gray-600 text-sm">
                If you're not getting showings or offers, the market is telling you something. Refusing to 
                adjust price or terms based on market feedback results in extended time on market and lower 
                final sale prices.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-center mb-3">
                <i className="ri-close-circle-line text-red-500 text-xl mr-2"></i>
                <h4 className="font-semibold text-gray-900">Emotional Decision Making</h4>
              </div>
              <p className="text-gray-600 text-sm">
                Letting emotions drive decisions rather than financial analysis leads to poor choices. Treat 
                the sale as a business transaction, not a personal matter. Make decisions based on data and 
                professional advice.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-center mb-3">
                <i className="ri-close-circle-line text-red-500 text-xl mr-2"></i>
                <h4 className="font-semibold text-gray-900">Inadequate Marketing</h4>
              </div>
              <p className="text-gray-600 text-sm">
                Relying on minimal marketing or single channels limits buyer exposure. Comprehensive, 
                multi-channel marketing generates more interest, competition, and better offers. Don't 
                skimp on marketing for million-dollar properties.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-center mb-3">
                <i className="ri-close-circle-line text-red-500 text-xl mr-2"></i>
                <h4 className="font-semibold text-gray-900">Inflexible Negotiation</h4>
              </div>
              <p className="text-gray-600 text-sm">
                Being unwilling to negotiate or compromise on any terms kills deals. Successful sellers know 
                when to stand firm and when to be flexible. Focus on your key priorities and be willing to 
                compromise on less important terms.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-center mb-3">
                <i className="ri-close-circle-line text-red-500 text-xl mr-2"></i>
                <h4 className="font-semibold text-gray-900">Ignoring Tax Planning</h4>
              </div>
              <p className="text-gray-600 text-sm">
                Failing to plan for tax implications can cost 30-40% of your profits. Consult with tax 
                professionals early to explore strategies like 1031 exchanges, installment sales, or 
                opportunity zone investments.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-center mb-3">
                <i className="ri-close-circle-line text-red-500 text-xl mr-2"></i>
                <h4 className="font-semibold text-gray-900">Accepting First Offer</h4>
              </div>
              <p className="text-gray-600 text-sm">
                Jumping at the first offer without proper evaluation or attempting to generate competition 
                often means leaving money on the table. Use first offers as leverage to attract additional 
                buyers and create bidding situations.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}